import type { MarketSession, MarketState } from './types'
import dayjs, { type Dayjs } from 'dayjs'

export type HolidayInfo = { name?: string; type?: string }

export function getSymbolMarketStatus({
  symbol,
  now,
  symbolHolidayMap,
  symbolSessionMap,
  symbolTimeZoneMap,
}: {
  symbol: string
  now: Dayjs
  symbolHolidayMap: Map<string, HolidayInfo>
  symbolSessionMap: Record<string, Record<number, MarketSession[]>>
  symbolTimeZoneMap: Record<string, string>
}): MarketState {
  const symbolTimeZone = symbolTimeZoneMap[symbol] ?? 'UTC'
  const serverClockNow = now.tz(symbolTimeZone)
  const dayIdx = serverClockNow.day()
  const symbolSessions = symbolSessionMap[symbol]
  const todaySessions = symbolSessions?.[dayIdx] ?? []
  const minuteOfNow = serverClockNow.hour() * 60 + serverClockNow.minute()

  // 节假日判断：只查 symbolHolidayMap
  const dateKey = now.format('YYYY-MM-DD')
  const isHoliday = symbolHolidayMap.has(dateKey)
  if (isHoliday) {
    const next = nextWeekdayOpen({
      serverClockNow,
      symbolHolidayMap,
      symbolSessions,
    })
    if (next) {
      return { isOpen: false, nextChange: next, nextOpen: next }
    } else {
      // 没有下一个开盘，返回当前时间或特殊处理
      return { isOpen: false, nextChange: serverClockNow, nextOpen: serverClockNow }
    }
  }

  // 今日交易时段
  for (const { openMinute, closeMinute, breaks, close } of todaySessions) {
    const inBreak = (breaks ?? []).some(
      (breakItem) =>
        breakItem.startMinute !== undefined &&
        breakItem.endMinute !== undefined &&
        minuteOfNow >= breakItem.startMinute &&
        minuteOfNow <= breakItem.endMinute,
    )
    if (
      openMinute !== undefined &&
      closeMinute !== undefined &&
      minuteOfNow >= openMinute &&
      minuteOfNow <= closeMinute &&
      !inBreak
    ) {
      const closeT = toTime(now, close)
      return { isOpen: true, nextChange: closeT, nextOpen: closeT }
    }
  }

  // 下一开盘
  const next = nextIntradayOpen(
    serverClockNow,
    minuteOfNow,
    todaySessions,
    symbolHolidayMap,
    symbolSessions,
  )
  if (next) {
    return { isOpen: false, nextChange: next, nextOpen: next }
  } else {
    // 没有下一个开盘，返回当前时间或特殊处理
    return { isOpen: false, nextChange: serverClockNow, nextOpen: serverClockNow }
  }
}

/**
 * 将时间点转为用户所在时区的原生 Date
 * @return 返回 Date，若需要 number 可再 `.getTime()`
 */
export function toUserDate(dt: Dayjs | string | Date, userTz?: string) {
  const tz = userTz || Intl.DateTimeFormat().resolvedOptions().timeZone
  return dayjs(dt).tz(tz).toDate()
}

/* ---------- helpers ---------- */
function toTime(base: Dayjs, hhmm: string) {
  const [h, m] = hhmm.split(':')
  return base.clone().hour(+h).minute(+m).second(0).millisecond(0)
}

function nextIntradayOpen(
  serverClockNow: Dayjs,
  minuteOfNow: number,
  todaySessions: MarketSession[],
  symbolHolidayMap: Map<string, HolidayInfo>,
  symbolSessions: Record<number, MarketSession[]>,
) {
  for (const s of todaySessions) {
    if (s.openMinute !== undefined && s.openMinute >= minuteOfNow)
      return toTime(serverClockNow, s.open)
  }
  return nextWeekdayOpen({
    serverClockNow,
    symbolHolidayMap,
    symbolSessions,
  })
}

function nextWeekdayOpen({
  serverClockNow,
  symbolHolidayMap,
  symbolSessions,
}: {
  serverClockNow: Dayjs
  symbolHolidayMap: Map<string, HolidayInfo>
  symbolSessions: Record<number, MarketSession[]>
}) {
  let probe = serverClockNow.add(1, 'day').startOf('day')
  let count = 0
  while (count < 365) {
    const sessions = symbolSessions[probe.day()] ?? []
    const dateKey = probe.format('YYYY-MM-DD')
    const isHoliday = symbolHolidayMap.has(dateKey)
    if (sessions?.length && !isHoliday) {
      return toTime(probe, sessions[0].open)
    }
    probe = probe.add(1, 'day')
    count++
  }
  return null
}

export function buildHolidayMap(
  holidays: Array<{ date: string; symbol?: string; name?: string; type?: string }>,
) {
  const map: { [symbol: string]: Map<string, HolidayInfo>; ALL: Map<string, HolidayInfo> } = {
    ALL: new Map(),
  }
  holidays.forEach((h) => {
    const info: HolidayInfo = { name: h.name, type: h.type }
    if (h.symbol) {
      if (!map[h.symbol]) map[h.symbol] = new Map()
      map[h.symbol].set(h.date, info)
    } else {
      map.ALL.set(h.date, info)
    }
  })
  return map
}

export const toHHMM = (m: number) => {
  const h = Math.floor(m / 60)
    .toString()
    .padStart(2, '0')
  const mm = (m % 60).toString().padStart(2, '0')
  return `${h}:${mm}`
}

/**
 * 上一行注释：把某个时间点格式化为用户本地时区（或指定时区）
 * @param dt    输入 Dayjs / Date / ISO 字符串
 * @param fmt   输出格式，默认 'YYYY-MM-DD HH:mm'
 * @param userTz 可选：IANA TZ，缺省则自动检测浏览器 `Intl.DateTimeFormat().resolvedOptions().timeZone`
 */
export function formatInUserTZ(
  dt: Dayjs | string | Date,
  fmt = 'YYYY-MM-DD HH:mm',
  userTz?: string,
) {
  const tz = userTz || Intl.DateTimeFormat().resolvedOptions().timeZone
  return dayjs(dt).tz(tz).format(fmt)
}
